// What a blueprint can be given, from the APIs the backend validates
// against (subagent_blueprint_ops._validate_refs): knowledge corpora from
// the registry, skills from the customer's library (lattice defaults +
// customer-scoped skills — the ones any agent may allowlist).
//
// Labels are the operator's words, never slugs (optionLabels.ts): a knowledge library reads as
// its project's name when a project owns it, else its name in words, with
// its document count in the hint. The value stays the id the server takes,
// so every picker (the New dialog, a subagent's Settings) gets this for free.

import { useCallback, useMemo } from "react";

import { useCorpora } from "@/api/knowledge";
import { useProjects } from "@/api/projects";
import { useActiveSkills, useSkillCatalog } from "@/api/skills";
import { PLUMBING } from "@/features/skills/catalog/builtIns";
import { displayName, skillLine } from "@/features/skills/catalog/catalogModel";
import { pluralize } from "@/lib/format";

import type { CheckOption } from "./fields";
import { libraryLabel, skillLabel } from "./optionLabels";

export interface CorpusOptions {
  options: CheckOption[];
  isLoading: boolean;
  /** A project's library index by project id, when it has one. */
  corpusOfProject: (projectId: string | null | undefined) => string | null;
  /** How many documents a library holds; null when it is not known (yet). */
  documentsOf: (corpus: string) => number | null;
  /** A library's own description ("" when it has none). */
  descriptionOf: (corpus: string) => string;
  /** The agents and subagents a library is open to by name. */
  agentsOf: (corpus: string) => readonly string[];
}

export function useCorpusOptions(customerId: string | null, { poll = true }: { poll?: boolean } = {}): CorpusOptions {
  const corpora = useCorpora(customerId, { poll });
  const projects = useProjects(customerId);
  const options = useMemo(() => {
    const projectName = new Map((projects.data?.projects ?? []).map((p) => [p.id, p.name] as const));
    return (corpora.data ?? []).map((c) => ({
      value: c.name,
      label: libraryLabel(c.name, c.project_id ? projectName.get(c.project_id) : null),
      hint: [c.description, pluralize(c.documents, "document")].filter(Boolean).join(" · "),
    }));
  }, [corpora.data, projects.data]);
  const byProject = useMemo(
    () => new Map((corpora.data ?? []).flatMap((c) => (c.project_id ? [[c.project_id, c.name] as const] : []))),
    [corpora.data],
  );
  const docs = useMemo(() => new Map((corpora.data ?? []).map((c) => [c.name, c.documents] as const)), [corpora.data]);
  const corpusOfProject = useCallback((projectId: string | null | undefined) => (projectId ? byProject.get(projectId) ?? null : null), [byProject]);
  const documentsOf = useCallback((corpus: string) => docs.get(corpus) ?? null, [docs]);
  const descriptions = useMemo(() => new Map((corpora.data ?? []).map((c) => [c.name, c.description ?? ""] as const)), [corpora.data]);
  const descriptionOf = useCallback((corpus: string) => descriptions.get(corpus) ?? "", [descriptions]);
  const scopes = useMemo(() => new Map((corpora.data ?? []).map((c) => [c.name, c.agents ?? []] as const)), [corpora.data]);
  const agentsOf = useCallback((corpus: string): readonly string[] => scopes.get(corpus) ?? [], [scopes]);
  return { options, isLoading: corpora.isLoading, corpusOfProject, documentsOf, descriptionOf, agentsOf };
}

/**
 * What a subagent can be given: the workspace's own skills and the built-in
 * ones, minus the plumbing a connection or project stands for (the list Settings hides),
 * each by its name in words (a built-in's plain name, a skill's own title, so
 * LP stays LP), never its id. Sorted by name.
 */
export function useSkillOptions(customerId: string | null): { options: CheckOption[]; isLoading: boolean } {
  const skills = useActiveSkills(customerId);
  const catalog = useSkillCatalog(customerId);
  const options = useMemo(() => {
    const rows = catalog.data?.skills ?? [];
    const named = new Map(rows.map((r) => [r.slug, r] as const));
    const seen = new Set<string>();
    const out: CheckOption[] = [];
    const add = (slug: string, label: string, line: string) => {
      if (seen.has(slug) || PLUMBING.includes(slug) || slug === "lattice-knowledge") return;
      seen.add(slug);
      out.push({ value: slug, label, hint: line });
    };
    for (const s of skills.data?.skills ?? []) {
      if (s.scope !== "customer") continue;
      const row = named.get(s.slug);
      add(s.slug, row ? displayName(row) : skillLabel(s.slug), row ? skillLine(row) : (s.description ?? ""));
    }
    for (const r of rows) if (r.group === "platform") add(r.slug, displayName(r), skillLine(r));
    return out.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [skills.data, catalog.data]);
  return { options, isLoading: skills.isLoading };
}
